/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useTenant } from "hooks/settings";
import { Loaded, loadedEndpoint } from "models/loaded";
import {
  useGetStrategyDashboardProductionQuery,
  TimeAggregation,
  StrategyDashboardProductionData,
} from "src/store/api/generatedApi";
import {
  GroupedProductionSeries,
  ProductionTimeSeries,
  ProductionTimeSeriesKey,
} from "../types";

export const useStrategyDashboardProduction = (
  start?: string,
  end?: string
): Loaded<GroupedProductionSeries> => {
  const loadedRawData = loadedEndpoint(
    useGetStrategyDashboardProductionQuery({
      tenantName: useTenant(),
      start,
      end,
    })
  );
  if (loadedRawData.status === "success") {
    return {
      status: "success",
      data: buildGroupedProductionSeries(loadedRawData.data),
      fetching: loadedRawData.fetching,
    };
  }
  return loadedRawData;
};

const buildGroupedProductionSeries = (
  data: StrategyDashboardProductionData
): GroupedProductionSeries => {
  const stringKeyKeyMap: Record<string, ProductionTimeSeriesKey> = {};
  const stringKeyValuesMap: Record<string, ProductionTimeSeries> = {};

  for (const item of data.series) {
    const key: ProductionTimeSeriesKey = {
      timeAggregation: item.time_aggregation,
      productionSource: item.production_source,
      productionType: item.production_type,
      productGroup: item.product_group_name,
    };
    const stringKey = JSON.stringify(key);
    stringKeyKeyMap[stringKey] = key;

    const value = stringKeyValuesMap[stringKey] ?? {
      date: [],
      produced_mass: [],
      heat_count: [],
    };
    value.date.push(item.production_date);
    value.produced_mass.push(item.produced_mass);
    value.heat_count.push(item.heat_count);
    stringKeyValuesMap[stringKey] = value;
  }

  const items: [ProductionTimeSeriesKey, ProductionTimeSeries][] =
    Object.entries(stringKeyKeyMap).map(([stringKey, key]) => [
      key,
      stringKeyValuesMap[stringKey]!,
    ]);

  const availableTimeAggregations = new Set<TimeAggregation>(
    data.series.map((item) => item.time_aggregation)
  );

  return {
    items,
    availableTimeAggregations,
    availableProductionTypes: data.production_types,
    availableProductionSources: data.production_sources,
    availableProductGroups: data.product_groups,
  };
};
